import { useEffect, useState } from "react";
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, CircularProgress,
    Container, Divider, Grid, IconButton, List, ListItem, ListItemIcon, ListItemText, Paper, TextField, Typography
} from "@mui/material";
import { Add, Delete, ExpandMore, Storefront } from "@mui/icons-material";
import { useAutenticacaoContext } from "../../../core/contexts/AutenticacaoContext";
import { Environment } from "../../../core/config/environment";
import { useOrganizacoes } from "../../hooks/useOrganizacoes";
import { IEstabelecimentoOrganizacao, IMembroOrganizacao, IOrganizacao } from "../../interfaces/IOrganizacao";

const formatarValor = (valor?: number | null) =>
    valor != null ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor) : null;

export const AdminOrganizacoes = () => {
    const { estabelecimento } = useAutenticacaoContext();
    const {
        listarOrganizacoes, criarOrganizacao, listarMembros, adicionarMembro, removerMembro, listarEstabelecimentos, loading
    } = useOrganizacoes();

    const ehSuperAdmin = estabelecimento?.uuid === Environment.UUID_SPACEFOOD;

    const [organizacoes, setOrganizacoes] = useState<IOrganizacao[]>([]);
    const [nome, setNome] = useState("");
    const [cnpj, setCnpj] = useState("");
    const [razaoSocial, setRazaoSocial] = useState("");

    const [membrosPorOrg, setMembrosPorOrg] = useState<Record<string, IMembroOrganizacao[]>>({});
    const [estabelecimentosPorOrg, setEstabelecimentosPorOrg] = useState<Record<string, IEstabelecimentoOrganizacao[]>>({});
    const [emailPorOrg, setEmailPorOrg] = useState<Record<string, string>>({});

    const carregarOrganizacoes = async () => {
        setOrganizacoes(await listarOrganizacoes());
    };

    useEffect(() => {
        if (ehSuperAdmin) {
            carregarOrganizacoes();
        }
    }, [ehSuperAdmin]);

    const handleCriar = async () => {
        if (!nome) return;
        const criada = await criarOrganizacao({ nome, cnpj: cnpj || undefined, razaoSocial: razaoSocial || undefined });
        if (criada) {
            setNome(""); setCnpj(""); setRazaoSocial("");
            await carregarOrganizacoes();
        }
    };

    const carregarMembros = async (organizacaoUuid: string) => {
        const membros = await listarMembros(organizacaoUuid);
        setMembrosPorOrg(prev => ({ ...prev, [organizacaoUuid]: membros }));
    };

    const carregarDetalhes = async (organizacaoUuid: string) => {
        const [membros, estabelecimentos] = await Promise.all([
            listarMembros(organizacaoUuid),
            listarEstabelecimentos(organizacaoUuid),
        ]);
        setMembrosPorOrg(prev => ({ ...prev, [organizacaoUuid]: membros }));
        setEstabelecimentosPorOrg(prev => ({ ...prev, [organizacaoUuid]: estabelecimentos }));
    };

    const handleAdicionarMembro = async (organizacaoUuid: string) => {
        const email = emailPorOrg[organizacaoUuid];
        if (!email) return;
        const ok = await adicionarMembro(organizacaoUuid, email);
        if (ok) {
            setEmailPorOrg(prev => ({ ...prev, [organizacaoUuid]: "" }));
            await carregarMembros(organizacaoUuid);
        }
    };

    const handleRemoverMembro = async (organizacaoUuid: string, usuarioUuid: string) => {
        const ok = await removerMembro(organizacaoUuid, usuarioUuid);
        if (ok) await carregarMembros(organizacaoUuid);
    };

    if (!ehSuperAdmin) {
        return (
            <Container>
                <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="h6">Acesso restrito ao administrador Space Food.</Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container>
            <Box sx={{ p: 2 }}>
                <Typography variant="h5" fontWeight="bold">Organizações (Space Food)</Typography>
                <Typography variant="body2" color="text.secondary">
                    Crie organizações e gerencie os usuários de cada uma.
                </Typography>

                <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, borderRadius: 3 }}>
                            <Typography variant="h6">Nova organização</Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                <TextField label="Nome*" size="small" value={nome} onChange={(e) => setNome(e.target.value)} />
                                <TextField label="CNPJ" size="small" value={cnpj} onChange={(e) => setCnpj(e.target.value)} />
                                <TextField label="Razão social" size="small" value={razaoSocial} onChange={(e) => setRazaoSocial(e.target.value)} />
                                <Button variant="contained" startIcon={<Add />} onClick={handleCriar} disabled={loading || !nome}>
                                    Criar organização
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="h6">Organizações</Typography>
                                {loading && <CircularProgress size={20} />}
                            </Box>
                            <Divider sx={{ my: 1.5 }} />
                            {organizacoes.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">Nenhuma organização cadastrada.</Typography>
                            ) : organizacoes.map((org) => (
                                <Accordion key={org.uuid} onChange={(_, expanded) => { if (expanded && !membrosPorOrg[org.uuid]) carregarDetalhes(org.uuid); }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                            <Typography fontWeight="bold">{org.nome}</Typography>
                                            {org.cnpj && <Chip size="small" label={org.cnpj} />}
                                            {estabelecimentosPorOrg[org.uuid] && (
                                                <Chip size="small" color="primary" icon={<Storefront />} label={`${estabelecimentosPorOrg[org.uuid].length} estab.`} />
                                            )}
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold">Estabelecimentos</Typography>
                                            <Chip size="small" label={estabelecimentosPorOrg[org.uuid]?.length ?? 0} />
                                        </Box>
                                        <List dense>
                                            {(estabelecimentosPorOrg[org.uuid] ?? []).map((est) => {
                                                const valor = formatarValor(est.valorAssinatura);
                                                const detalhe = [est.planoNome, valor].filter(Boolean).join(" • ");
                                                return (
                                                    <ListItem key={est.uuid} divider
                                                        secondaryAction={est.planoNome && <Chip size="small" label={est.planoNome} variant="outlined" />}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 40 }}><Storefront color="action" /></ListItemIcon>
                                                        <ListItemText primary={est.nomeFantasia} secondary={detalhe || est.cnpj || est.razaoSocial} />
                                                    </ListItem>
                                                );
                                            })}
                                            {(estabelecimentosPorOrg[org.uuid]?.length ?? 0) === 0 && (
                                                <Typography variant="caption" color="text.secondary">Nenhum estabelecimento vinculado.</Typography>
                                            )}
                                        </List>

                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Usuários</Typography>
                                        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                                            <TextField
                                                label="E-mail do usuário"
                                                size="small"
                                                fullWidth
                                                value={emailPorOrg[org.uuid] ?? ""}
                                                onChange={(e) => setEmailPorOrg(prev => ({ ...prev, [org.uuid]: e.target.value }))}
                                            />
                                            <Button variant="contained" onClick={() => handleAdicionarMembro(org.uuid)} disabled={loading || !emailPorOrg[org.uuid]}>
                                                Adicionar
                                            </Button>
                                        </Box>
                                        <List dense>
                                            {(membrosPorOrg[org.uuid] ?? []).map((membro) => (
                                                <ListItem
                                                    key={membro.usuarioUuid}
                                                    secondaryAction={
                                                        <IconButton edge="end" size="small" color="error" onClick={() => handleRemoverMembro(org.uuid, membro.usuarioUuid)}>
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    }
                                                >
                                                    <ListItemText primary={membro.nome ?? membro.email} secondary={membro.email} />
                                                </ListItem>
                                            ))}
                                            {(membrosPorOrg[org.uuid]?.length ?? 0) === 0 && (
                                                <Typography variant="caption" color="text.secondary">Nenhum membro ainda.</Typography>
                                            )}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};
